import type { EventDataItem } from './types';
import { resolveEventTypeDisplayOrderKey } from './utils';

export interface IEventRecordDisplayNames {
  nameEn: string;
  nameCn: string;
  name: string;
  name_cn: string;
}

/** 与 EventWindows 展示、getOnlyOneKey 筛选比对共用的 name 解析（en/cn 互兜底，空字符串视为无值） */
export function resolveEventRecordDisplayNames(
  record: Record<string, unknown>,
): IEventRecordDisplayNames {
  const nameEn = String(record.event_title_en ?? record.name ?? '').trim();
  const nameCn = String(record.event_title_cn ?? record.name_cn ?? '').trim();
  return {
    nameEn,
    nameCn,
    name: nameEn || nameCn,
    name_cn: nameCn || nameEn,
  };
}

/** 与 UI 分组 / 筛选 key 对齐的 event_type 字符串 */
export function resolveEventRecordKeyEventType(
  record: Record<string, unknown>,
  blockEventType?: unknown,
): string {
  return (
    resolveEventTypeDisplayOrderKey(
      record.event_type ?? record.eventType ?? blockEventType,
    ) ||
    String(record.event_type ?? record.eventType ?? blockEventType ?? '')
  );
}

/** getOnlyOneKey 名称段：禁止 `event_title_en ?? name`（空字符串会阻断 cn 兜底） */
export function resolveEventRecordKeyName(
  record: Record<string, unknown>,
): string {
  const { nameEn, nameCn } = resolveEventRecordDisplayNames(record);
  return nameEn || nameCn;
}

export interface INormalizeRecordForEventWindowsDisplayOptions {
  /** date_event_list 块级 event_type，record 自身无类型时兜底 */
  blockEventType?: unknown;
  /**
   * 是否清空 description / description_cn，避免舆情 EventItem 内 CommonBubble 重复 hover。
   * EventWindows 展示默认 true。
   */
  clearDescriptionHoverFields?: boolean;
}

/**
 * EventWindows / EventDataMap 单条事件展示归一化（KPI 与 Overview 共用）。
 * - name / name_cn：event_title_* 与 name 字段互兜底
 * - event_type：resolveEventTypeDisplayOrderKey 对齐 UI 分组键
 * - description 置空：条上已展示 name，避免双层浮窗
 */
export function normalizeRecordForEventWindowsDisplay(
  record: Record<string, unknown>,
  options?: INormalizeRecordForEventWindowsDisplayOptions,
): EventDataItem {
  const blockEventType = options?.blockEventType;
  const clearDescriptionHoverFields =
    options?.clearDescriptionHoverFields !== false;

  const { name, name_cn: nameCn } = resolveEventRecordDisplayNames(record);
  const typeKey = resolveEventRecordKeyEventType(record, blockEventType);

  return {
    ...(record as EventDataItem),
    event_type: typeKey,
    name,
    name_cn: nameCn,
    event_desc_en: String(record.event_desc_en ?? ''),
    event_desc_cn: String(record.event_desc_cn ?? ''),
    ...(clearDescriptionHoverFields
      ? {
        description: '',
        description_cn: '',
      }
      : {}),
  };
}
